(function () {
  const instances = new Map();

  class MachineBanner {
    constructor(section) {
      this.section = section;
      this.defaultModule = section.dataset.defaultModule || '';
      this.activeModule = null;

      this.tabs = Array.from(section.querySelectorAll('[data-module-target]'));
      this.mediaItems = this.collectElements('[data-module-name]', 'moduleName');
      this.contentItems = this.collectElements('[data-module-content]', 'moduleContent');
      this.detailItems = this.collectElements('[data-module-detail]', 'moduleDetail');
      this.buttons = this.collectElements('[data-module-button]', 'moduleButton');
      this.titles = this.collectElements('[data-module-title]', 'moduleTitle');
      this.videos = new Map();

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);

      this.init();
    }

    collectElements(selector, datasetKey) {
      const map = new Map();
      this.section.querySelectorAll(selector).forEach((element) => {
        const key = element.dataset[datasetKey];
        if (key) {
          map.set(key, element);
        }
      });
      return map;
    }

    /**
     * 获取当前可见的视频元素（根据屏幕尺寸选择移动端或桌面端视频）
     * @param {Array|HTMLElement} videos - 视频元素数组或单个视频元素
     * @returns {HTMLElement|null} 当前可见的视频元素
     */
    getVisibleVideo(videos) {
      if (!videos) return null;
      
      // 如果传入的是单个元素，直接返回
      if (!Array.isArray(videos)) {
        return videos;
      }
      
      // 如果是数组，查找当前可见的视频
      for (const video of videos) {
        const style = window.getComputedStyle(video);
        if (style.display !== 'none' && style.visibility !== 'hidden') {
          return video;
        }
      }
      
      // 如果没有可见的视频，根据屏幕尺寸选择应该使用的视频
      const isMobile = window.innerWidth <= 989;
      for (const video of videos) {
        const isMobileVideo = video.classList.contains('machine-banner__video--mobile');
        const isDesktopVideo = video.classList.contains('machine-banner__video--desktop');
        
        if (isMobile && isMobileVideo) {
          return video;
        }
        if (!isMobile && isDesktopVideo) {
          return video;
        }
      }
      
      // 如果没有找到匹配的，返回第一个（作为后备）
      return videos[0] || null;
    }

    init() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick);
        tab.addEventListener('keydown', this.handleTabKeydown);
        // 添加鼠标悬停预加载
        tab.addEventListener('mouseenter', this.handleTabHover.bind(this));
      });

      // 收集视频元素（包括移动端和桌面端）
      this.mediaItems.forEach((element, key) => {
        const videos = element.querySelectorAll('[data-module-video]');
        if (videos.length > 0) {
          // 存储所有视频元素，JavaScript 会根据当前屏幕尺寸选择可见的视频
          this.videos.set(key, Array.from(videos));
        }
      });

      const initialModule = this.findInitialModule();
      
      // 立即激活模块，显示内容
      this.activateModule(initialModule, false);
      
      // 标记为就绪，应用过渡效果
      requestAnimationFrame(() => {
        this.section.classList.add('machine-banner--ready');
        
        // 确保默认模块的视频开始加载
        const initialVideos = this.videos.get(initialModule);
        if (initialVideos) {
          const videoArray = Array.isArray(initialVideos) ? initialVideos : [initialVideos];
          const visibleVideo = this.getVisibleVideo(videoArray);
          if (visibleVideo && visibleVideo.readyState === 0) {
            // 如果视频还没开始加载，手动触发
            visibleVideo.load();
          }
        }
        
        // 延迟预加载其他模块的视频（移动端也能受益）
        // 等待默认模块加载完成后再预加载其他模块
        setTimeout(() => {
          this.preloadOtherVideos(initialModule);
        }, 1000);
      });
    }

    handleTabHover(event) {
      const targetModule = event.currentTarget.dataset.moduleTarget;
      if (targetModule && targetModule !== this.activeModule) {
        // 预加载悬停模块的视频
        this.preloadVideo(targetModule);
      }
    }

    preloadVideo(moduleKey) {
      const videos = this.videos.get(moduleKey);
      if (!videos) return;
      
      // 预加载所有视频（移动端和桌面端）
      const videoArray = Array.isArray(videos) ? videos : [videos];
      videoArray.forEach((video) => {
        if (video.dataset.videoLoaded === 'true') {
          return;
        }

        // 准备视频
        this.prepareVideo(video);

        // 如果视频还没有开始加载或只有元数据，开始加载更多数据
        if (video.readyState <= 1) {
          video.load();
        }
      });
    }

    preloadOtherVideos(excludeModuleKey) {
      // 预加载其他模块的视频，提升切换速度
      this.videos.forEach((videos, moduleKey) => {
        if (moduleKey === excludeModuleKey) return;
        
        const videoArray = Array.isArray(videos) ? videos : [videos];
        videoArray.forEach((video) => {
          if (video.readyState <= 1) {
            this.prepareVideo(video);
            video.load();
          }
        });
      });
    }

    destroy() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.handleTabClick);
        tab.removeEventListener('keydown', this.handleTabKeydown);
      });

      this.videos.forEach((videos) => {
        const videoArray = Array.isArray(videos) ? videos : [videos];
        videoArray.forEach((video) => {
          if (video._machineBannerEndedHandler) {
            video.removeEventListener('ended', video._machineBannerEndedHandler);
            video._machineBannerEndedHandler = null;
          }
        });
      });

      this.videos.clear();
    }

    handleTabClick(event) {
      const target = event.currentTarget.dataset.moduleTarget;
      if (target) {
        this.activateModule(target, true);
      }
    }

    handleTabKeydown(event) {
      const { key } = event;
      if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'ArrowUp' && key !== 'ArrowDown') {
        return;
      }

      event.preventDefault();

      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex === -1) {
        return;
      }

      const direction = key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 1;
      const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
      const nextTab = this.tabs[nextIndex];

      if (nextTab) {
        nextTab.focus();
        this.activateModule(nextTab.dataset.moduleTarget, true);
      }
    }

    findInitialModule() {
      if (this.defaultModule && this.mediaItems.has(this.defaultModule)) {
        return this.defaultModule;
      }

      if (this.tabs.length > 0) {
        return this.tabs[0].dataset.moduleTarget;
      }

      const firstMedia = this.mediaItems.keys().next();
      if (!firstMedia.done) {
        return firstMedia.value;
      }

      return '';
    }

    activateModule(moduleKey, userInitiated) {
      if (!moduleKey || this.activeModule === moduleKey) {
        return;
      }

      this.activeModule = moduleKey;

      this.updateTabs(moduleKey);
      this.toggleGroup(this.mediaItems, moduleKey);
      this.toggleGroup(this.contentItems, moduleKey);
      this.toggleGroup(this.detailItems, moduleKey);
      this.toggleGroup(this.buttons, moduleKey);
      this.toggleGroup(this.titles, moduleKey);

      this.handleMediaState(moduleKey);

      if (userInitiated) {
        this.section.dispatchEvent(
          new CustomEvent('machine-banner:change', {
            bubbles: true,
            detail: { module: moduleKey }
          })
        );
      }
    }

    updateTabs(activeKey) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.moduleTarget === activeKey;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    toggleGroup(map, activeKey) {
      map.forEach((element, key) => {
        const isActive = key === activeKey;
        element.classList.toggle('is-active', isActive);
        if (element.hasAttribute('aria-hidden')) {
          element.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        }
      });
    }

    handleMediaState(activeKey) {
      // 处理视频状态：激活时加载并播放，非激活时暂停
      this.videos.forEach((videos, key) => {
        if (!videos) {
          return;
        }

        const videoArray = Array.isArray(videos) ? videos : [videos];
        
        if (key === activeKey) {
          // 激活模块：只加载并播放当前可见的视频
          const visibleVideo = this.getVisibleVideo(videoArray);
          if (visibleVideo) {
            this.loadAndPlayVideo(visibleVideo, key);
          }
        } else {
          // 非激活模块：暂停所有视频
          videoArray.forEach((video) => {
            this.resetVideo(video, key);
            // 如果视频已加载，显示占位符
            if (video.dataset.videoLoaded === 'true') {
              this.showPoster(key);
            }
          });
        }
      });
    }

    prepareVideo(video) {
      if (!video || video.dataset.videoLoaded === 'true') {
        return;
      }

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('x5-playsinline', '');
      video.autoplay = false;

      const stayLast = video.dataset.videoStayLast === 'true';
      const loop = video.dataset.videoLoop === 'true';

      if (!loop && stayLast) {
        const handleEnded = () => {
          try {
            video.pause();
            if (video.duration && !Number.isNaN(video.duration)) {
              video.currentTime = Math.max(video.duration - 0.1, 0);
            }
          } catch (error) {
            console.warn('[MachineBanner] Failed to freeze video on last frame.', error);
          }
        };

        video._machineBannerEndedHandler = handleEnded;
        video.addEventListener('ended', handleEnded);
      }
    }

    loadAndPlayVideo(video, moduleKey) {
      if (!video) {
        return;
      }

      const isLoaded = video.dataset.videoLoaded === 'true';
      const readyState = video.readyState;
      
      // 准备视频（如果还没准备）
      this.prepareVideo(video);
      
      if (!isLoaded) {
        // 如果视频还没开始加载，先调用 load()
        if (readyState === 0) {
          video.load();
        }
        
        // 检查视频是否已经有足够的缓冲可以播放
        // readyState 3 = HAVE_FUTURE_DATA, 4 = HAVE_ENOUGH_DATA
        const canPlayNow = readyState >= 3;
        
        if (canPlayNow) {
          // 视频已经有足够数据，可以直接播放
          video.dataset.videoLoaded = 'true';
          this.hidePoster(moduleKey);
          this.playVideo(video);
          return;
        }

        // 尝试立即播放（即使数据还不够，浏览器可能会缓冲）
        // 这样可以减少等待时间
        const tryPlay = () => {
          this.playVideo(video).then(() => {
            // 播放成功，隐藏占位符
            video.dataset.videoLoaded = 'true';
            this.hidePoster(moduleKey);
          }).catch(() => {
            // 播放失败，等待更多数据
          });
        };
        
        // 立即尝试播放
        tryPlay();
        
        let loadedHandled = false;
        
        const handleLoaded = () => {
          if (loadedHandled) return;
          loadedHandled = true;
          
          video.dataset.videoLoaded = 'true';
          
          // 隐藏占位符
          this.hidePoster(moduleKey);
          
          // 确保视频正在播放
          if (video.paused && !video.ended) {
            this.playVideo(video);
          }
        };

        const handleError = () => {
          console.warn('[MachineBanner] Video failed to load for module:', moduleKey);
          // 视频加载失败时，保持占位符显示
        };

        // 监听多个事件确保视频加载完成
        video.addEventListener('canplay', handleLoaded, { once: true });
        video.addEventListener('canplaythrough', handleLoaded, { once: true });
        video.addEventListener('error', handleError, { once: true });
        
        // 如果已经有 loadeddata 数据，也触发
        if (readyState >= 2) {
          handleLoaded();
        }
      } else {
        // 已加载：隐藏占位符并直接播放
        this.hidePoster(moduleKey);
        this.playVideo(video);
      }
    }

    hidePoster(moduleKey) {
      const mediaItem = this.mediaItems.get(moduleKey);
      if (mediaItem) {
        const poster = mediaItem.querySelector('[data-module-poster]');
        if (poster) {
          poster.style.opacity = '0';
          setTimeout(() => {
            poster.style.display = 'none';
          }, 300);
        }
      }
    }

    showPoster(moduleKey) {
      const mediaItem = this.mediaItems.get(moduleKey);
      if (mediaItem) {
        const poster = mediaItem.querySelector('[data-module-poster]');
        if (poster) {
          poster.style.display = '';
          requestAnimationFrame(() => {
            poster.style.opacity = '1';
          });
        }
      }
    }

    playVideo(video) {
      if (!video) {
        return Promise.reject(new Error('Video element is null'));
      }

      try {
        // 如果视频已经播放到末尾，重置到开头
        if (video.ended || video.currentTime >= (video.duration - 0.5)) {
          video.currentTime = 0;
        }
      } catch (error) {
        console.warn('[MachineBanner] Unable to reset video time before play.', error);
      }

      const playPromise = video.play();

      if (playPromise && typeof playPromise.then === 'function') {
        return playPromise.catch((error) => {
          // 如果不是用户手势错误，才打印警告
          if (error.name !== 'NotAllowedError') {
            console.warn('[MachineBanner] Video play failed:', error);
          }
          throw error;
        });
      }

      return Promise.resolve();
    }

    resetVideo(video, moduleKey) {
      if (!video) {
        return;
      }

      video.pause();

      if (video.loop) {
        try {
          video.currentTime = 0;
        } catch (error) {
          console.warn('[MachineBanner] Unable to reset loop video.', error);
        }
        return;
      }

      try {
        video.currentTime = 0;
      } catch (error) {
        console.warn('[MachineBanner] Unable to reset video.', error);
      }
    }
  }

  function initComponent(section) {
    if (!section || instances.has(section)) {
      return;
    }

    const instance = new MachineBanner(section);
    instances.set(section, instance);
  }

  function destroyComponent(section) {
    const instance = instances.get(section);
    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(section);
  }

  function initAll(context = document) {
    const sections = context.querySelectorAll('.machine-banner');
    sections.forEach((section) => initComponent(section));
  }

  document.addEventListener('DOMContentLoaded', () => {
    initAll();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sections = event.target.querySelectorAll('.machine-banner');
    sections.forEach((section) => destroyComponent(section));
  });
})();
